// Move-to-Team confirmation (RFC-1371 D3).
//
// A modal rather than the two-click arm/fire button this replaces. Publishing
// is irreversible — there is deliberately no unpublish route — and a second
// click on the same control cannot say WHY it is guarded, nor offer a way out
// beyond waiting three seconds for the arming to lapse.
//
// Its own component, not inline in HomePage, so the interaction can be tested
// against the real markup. Asserting it through HomePage would mean mounting
// dnd-kit, routing and a dozen query hooks to check four things about one
// dialog; reimplementing it in a test harness would assert nothing at all.
//
// Not styled as destructive: nothing is lost, it is shared. But it IS
// permanent, which is what the body says and why the primary button carries
// the action rather than "OK".

import { useTranslation } from 'react-i18next'

import Button from '../ui/Button'

export default function MoveToTeamDialog({
  projectName,
  isMoving,
  onCancel,
  onConfirm,
}: {
  projectName: string
  isMoving: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation('pages')
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="move-to-team-title"
      data-testid="move-to-team-dialog"
    >
      <div className="bg-panel rounded-lg p-6 max-w-md mx-4 shadow-2xl border border-border animate-scale-in">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 bg-primary/10 rounded-md border border-primary/20">
            <svg
              className="w-6 h-6 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <div>
            <h3 id="move-to-team-title" className="text-xl font-bold text-white">
              {t('home.moveToTeamConfirmTitle')}
            </h3>
            <p className="text-text-secondary mt-2 text-sm">
              {t('home.moveToTeamConfirmMessage', { name: projectName })}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isMoving}
            data-testid="move-to-team-cancel"
          >
            {t('home.cancel')}
          </Button>
          <Button
            variant="default"
            onClick={onConfirm}
            loading={isMoving}
            data-testid="move-to-team-confirm"
          >
            {t('home.moveToTeamConfirmAction')}
          </Button>
        </div>
      </div>
    </div>
  )
}
